/**
 * Toast component - A dismissible notification message
 * Use for success, error, or info feedback
 */

import React from "react";
import { css } from "../../lib/panda";

export interface ToastProps {
  /** Message to display */
  message: string;
  /** Visual variant */
  variant?: "success" | "error" | "info";
  /** Called when the close button is clicked */
  onClose: () => void;
  /** Optional additional content (e.g. links, actions) */
  children?: React.ReactNode;
  /** Optional custom styles */
  style?: React.CSSProperties;
}

const variantStyles = {
  success: {
    backgroundColor: "#dcfce7",
    border: "1px solid #86efac",
    color: "#166534",
  },
  error: {
    backgroundColor: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#dc2626",
  },
  info: {
    backgroundColor: "#dbeafe",
    border: "1px solid #93c5fd",
    color: "#1e40af",
  },
};

export const Toast = React.forwardRef<HTMLDivElement, ToastProps>(
  (
    {
      message,
      variant = "info",
      onClose,
      children,
      style,
      ...props
    },
    ref
  ) => {
    const baseStyles = css({
      padding: "0.75rem 1rem",
      borderRadius: "0.5rem",
      fontSize: "0.9375rem",
      display: "flex",
      alignItems: "center",
      gap: "0.75rem",
      flexWrap: "wrap",
      boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
      position: "fixed",
      bottom: "1.5rem",
      right: "1.5rem",
      maxWidth: "min(400px, calc(100vw - 3rem))",
      zIndex: 9999,
      ...variantStyles[variant],
    });

    return (
      <div ref={ref} style={{ ...baseStyles, ...style }} {...props}>
        <span style={{ flex: 1, minWidth: 0 }}>{message}</span>
        {children && <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>{children}</span>}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "1.5rem",
            height: "1.5rem",
            padding: 0,
            marginLeft: "0.25rem",
            border: "none",
            borderRadius: "0.25rem",
            backgroundColor: "rgba(0,0,0,0.08)",
            color: "inherit",
            cursor: "pointer",
            fontSize: "1.25rem",
            lineHeight: 1,
            flexShrink: 0,
          }}
        >
          ×
        </button>
      </div>
    );
  }
);

Toast.displayName = "Toast";
